import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import InternalHeader from '../InternalHeader';
import Sidebar from '../Sidebar';
import DataTable from 'react-data-table-component';
import { FaTrash, FaSave } from 'react-icons/fa';
import './CreateTreatment.css';

const CreateTreatment = () => {
    const navigate = useNavigate();
    const [isSidebarVisible, setIsSidebarVisible] = useState(true);
    const [activeTab, setActiveTab] = useState('Treatments'); // Default active tab
    const [treatmentItems, setTreatmentItems] = useState([]); // State for treatment items
    const [inventoryItems, setInventoryItems] = useState([]); // State for inventory items
    const [inventoryList, setInventoryList] = useState([]); // State for fetched inventory items
    const [notes, setNotes] = useState(''); // State for notes

    const toggleSidebar = () => {
        setIsSidebarVisible(!isSidebarVisible);
    };

    const [treatment, setTreatment] = useState({
        Legend: '',
        Treatmentname: '',
        Cost: '',
    });

    const [errors, setErrors] = useState({});

    // Fetch inventory items from the API
    useEffect(() => {
        const fetchInventory = async () => {
            try {
                const response = await fetch('https://api.hgcdental.clinic/api/inventory');
                if (!response.ok) throw new Error('Failed to fetch inventory');
                const data = await response.json();
                setInventoryList(data); // Set the fetched inventory items
            } catch (err) {
                console.error('Error fetching inventory:', err);
            }
        };

        fetchInventory();
    }, []);

    const validateForm = () => {
        const newErrors = {};

        if (!treatment.Legend.trim()) newErrors.Legend = 'Legend is required';
        if (!treatment.Treatmentname.trim()) newErrors.Treatmentname = 'Treatment Name is required';
        if (!treatment.Cost || isNaN(treatment.Cost)) newErrors.Cost = 'Cost is required and must be a number';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validateInventoryItems = () => {
        for (const item of inventoryItems) {
            if (!item.inventoryId || !item.Quantity || isNaN(item.Quantity)) {
                return false;
            }
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate the form fields
        if (!validateForm()) {
            alert('Please fill out all required fields correctly.');
            return;
        }

        // Validate inventory items
        if (!validateInventoryItems()) {
            alert('Please ensure all inventory items are valid.');
            return;
        }

        try {
            // Fetch existing treatments from the API
            const treatmentsResponse = await fetch('https://api.hgcdental.clinic/api/treatments');
            if (!treatmentsResponse.ok) throw new Error('Failed to fetch treatments');
            const existingTreatments = await treatmentsResponse.json();

            // Check if the Legend already exists
            const isLegendDuplicate = existingTreatments.some(
                (existingTreatment) => existingTreatment.Legend === treatment.Legend
            );

            if (isLegendDuplicate) {
                alert('Error: The legend already exists. Please use a different legend.');
                return;
            }

            // Prepare the treatment data for submission
            const treatmentData = {
                Legend: treatment.Legend,
                Treatmentname: treatment.Treatmentname,
                Cost: treatment.Cost,
                Inventory: inventoryItems.map((item) => ({
                    inventoryId: item.inventoryId,
                    Quantity: item.Quantity,
                })),
                Notes: notes,
            };

            // Submit the treatment data
            const response = await fetch('https://api.hgcdental.clinic/api/treatments', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(treatmentData),
            });

            const json = await response.json();

            if (response.ok) {
                navigate('/treatment'); // Redirect to treatment list page
            } else {
                console.error('Error:', json.error);
                alert('An error occurred while creating the treatment.');
            }
        } catch (err) {
            console.error('Error submitting treatment:', err);
            alert('An error occurred. Please try again later.');
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        setTreatment((prevState) => ({
            ...prevState,
            [name]: value,
        }));

        setErrors((prevErrors) => ({
            ...prevErrors,
            [name]: '',
        }));
    };

    // Tab handling
    const handleTabClick = (tabName) => {
        setActiveTab(tabName);
    };

    // Add treatment item
    const addTreatmentsItem = () => {
        setTreatmentItems([
            ...treatmentItems,
            {
                treatmentId: null,
                legend: '',
                treatmentName: '',
            },
        ]);
    };

    // Delete treatment item
    const handleDeleteTreatment = (index) => {
        const updatedItems = treatmentItems.filter((_, i) => i !== index);
        setTreatmentItems(updatedItems);
    };

    // Handle treatment change
    const handleTreatmentChange = (index, treatmentId) => {
        const selectedTreatment = treatmentItems.find((treatment) => treatment._id === treatmentId);
        if (selectedTreatment) {
            const updatedItems = treatmentItems.map((item, i) =>
                i === index
                    ? {
                        ...item,
                        treatmentId,
                        legend: selectedTreatment.Legend,
                        treatmentName: selectedTreatment.Treatmentname,
                    }
                    : item
            );
            setTreatmentItems(updatedItems);
        }
    };

    // Add inventory item
    const addInventoryItem = () => {
        setInventoryItems([
            ...inventoryItems,
            {
                id: Date.now(), // Unique ID for each item
                inventoryId: '', // Initialize with an empty string
                Name: '',
                Quantity: 1,
                Unit: '',
            },
        ]);
    };

    // Delete inventory item
    const handleDeleteInventory = (index) => {
        const updatedItems = inventoryItems.filter((_, i) => i !== index);
        setInventoryItems(updatedItems);
    };

    // Handle inventory name change (dropdown selection)
    const handleInventoryNameChange = (index, inventoryId) => {
        const selectedItem = inventoryList.find(item => item._id === inventoryId);
        if (selectedItem) {
            const updatedItems = inventoryItems.map((item, i) =>
                i === index
                    ? {
                        ...item,
                        inventoryId: selectedItem._id,
                        Name: selectedItem.Name,
                        Unit: selectedItem.Unit,
                    }
                    : item
            );
            setInventoryItems(updatedItems);
        }
    };

    // Handle inventory quantity change
    const handleInventoryQuantityChange = (index, value) => {
        const updatedItems = inventoryItems.map((item, i) =>
            i === index ? { ...item, Quantity: Math.max(1, value) } : item
        );
        setInventoryItems(updatedItems);
    };

    // Handle notes edit
    const handleEditNotes = () => {
        alert('Edit notes functionality to be implemented.');
    };

    // Table columns for inventory
    const inventoryColumns = [
        {
            name: 'Name',
            cell: (row, index) => (
                <select
                    value={row.inventoryId || ''}
                    onChange={(e) => handleInventoryNameChange(index, e.target.value)}
                >
                    <option value="" disabled>
                        Select Inventory
                    </option>
                    {inventoryList.map((item) => (
                        <option key={item._id} value={item._id}>
                            {item.Name}
                        </option>
                    ))}
                </select>
            ),
        },
        {
            name: 'Quantity',
            cell: (row, index) => (
                <input
                    type="number"
                    value={row.Quantity}
                    min="1"
                    onChange={(e) => handleInventoryQuantityChange(index, parseInt(e.target.value, 10))}
                />
            ),
        },
        {
            name: 'Unit',
            cell: (row) => (
                <span>{row.Unit}</span>
            ),
        },
        {
            name: 'Actions',
            cell: (row, index) => (
                <FaTrash
                    className="action-icon"
                    style={{ color: 'red', cursor: 'pointer' }}
                    onClick={() => handleDeleteInventory(index)}
                />
            ),
        },
    ];

    // Custom styles for DataTable
    const customStyles = {
        rows: {
            style: {
                borderBottom: '1px solid #ddd',
            },
        },
        headCells: {
            style: {
                backgroundColor: '#007bff',
                color: 'white',
                fontWeight: 'bold',
                padding: '15px 10px',
                textAlign: 'center',
            },
        },
        cells: {
            style: {
                padding: '12px 10px',
                borderRight: '1px solid #ddd',
            },
        },
    };

    return (
        <div className={`create-patient-container ${isSidebarVisible ? 'sidebar-visible' : 'sidebar-hidden'}`}>
            <InternalHeader moduleName="Create Treatment" toggleSidebar={toggleSidebar} />
            <div className="create-patient-content">
                <Sidebar isSidebarVisible={isSidebarVisible} />
                <div className="create-patient">
                    <div>
                        <button className="create-patient-button" onClick={handleSubmit}>
                            <FaSave /> Save
                        </button>
                    </div>
                    <div className="create-patient-form">
                        <h2>Create New Treatment</h2>

                        {/* Legend Field */}
                        <div className="input-group">
                            <label className="label">Legend</label>
                            <input type="text" name="Legend" value={treatment.Legend} onChange={handleChange} className="input-field" />
                            {errors.Legend && <span className="error-message">{errors.Legend}</span>}
                        </div>

                        {/* Treatment Name Field */}
                        <div className="input-group">
                            <label className="label">Treatment Name</label>
                            <input type="text" name="Treatmentname" value={treatment.Treatmentname} onChange={handleChange} className="input-field" />
                            {errors.Treatmentname && <span className="error-message">{errors.Treatmentname}</span>}
                        </div>

                        {/* Cost Field */}
                        <div className="input-group">
                            <label className="label">Cost</label>
                            <input type="number" name="Cost" value={treatment.Cost} onChange={handleChange} className="input-field" />
                            {errors.Cost && <span className="error-message">{errors.Cost}</span>}
                        </div>

                        {/* Tab Navigation */}
                        <div className="tab-navigation">
                            <span
                                onClick={() => handleTabClick('Inventory')}
                                className={`tab ${activeTab === 'Inventory' ? 'active' : ''}`}
                            >
                                Inventory
                            </span>
                            <span
                                onClick={() => handleTabClick('Notes')}
                                className={`tab ${activeTab === 'Notes' ? 'active' : ''}`}
                            >
                                Notes
                            </span>
                        </div>

                        {/* Tab Content */}
                        <div className="tab-content">
                            {activeTab === 'Inventory' && (
                                <div className="invoice-tab">
                                    <button onClick={addInventoryItem} className="create-button-table">
                                        Add Inventory
                                    </button>
                                    <button onClick={handleSubmit} className="create-button-table">
                                        <FaSave /> Save
                                    </button>
                                    <div className="modal-divider-table" />
                                    <div className="table-form-table">
                                        <DataTable
                                            columns={inventoryColumns}
                                            data={inventoryItems}
                                            highlightOnHover
                                            striped
                                            customStyles={customStyles}
                                        />
                                    </div>
                                </div>
                            )}
                            {activeTab === 'Notes' && (
                                <div className="patient-details-tab">
                                    <h2>Notes</h2>
                                    <textarea
                                        className="prescription-input"
                                        placeholder="Write Notes here..."
                                        value={notes}
                                        onChange={(e) => setNotes(e.target.value)}
                                    ></textarea>
                                    <button onClick={handleEditNotes} className="details-button">
                                        Edit
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CreateTreatment;